import AddNewUser from '@/views/AddNewUser'
import AddWatch from '@/views/AddWatch'
import AgencyUsageReport from '@/views/AgencyUsageReport'
import AgencyUsageSearch from '@/views/AgencyUsageSearch'
import ChangePassword from '@/views/ChangePassword'
import CompleteSignup from '@/views/CompleteSignup'
import Dashboard from '@/views/Dashboard'
import EditProfile from '@/views/EditProfile'
import ForgotPassword from '@/views/ForgotPassword'
import ForgotUsername from '@/views/ForgotUsername'
import GeoActivityResults from '@/views/GeoActivityResults'
import GeoActivitySearch from '@/views/GeoActivitySearch'
import Login from '@/views/Login'
import MultipleIdResults from '@/views/MultipleIdResults'
import MultipleIdSearch from '@/views/MultipleIdSearch'
import MyAccount from '@/views/MyAccount'
import MySearches from '@/views/MySearches'
import MyReports from '@/views/MyReports'
import PersonSearch from '@/views/PersonSearch'
import PersonSummary from '@/views/PersonSummary'
import SplitById from '@/views/SplitById'
import SplitByIdResults from '@/views/SplitByIdResults'
import SplitByBio from '@/views/SplitByBio'
import SplitTwins from '@/views/SplitTwins'
import SplitByBioResults from '@/views/SplitByBioResults'
import ProximityResults from '@/views/ProximityResults'
import Registration from '@/views/Registration'
import Router from 'vue-router'
import SearchResults from '@/views/SearchResults'
import StoreActivityReport from '@/views/StoreActivityReport'
import StoreActivitySearch from '@/views/StoreActivitySearch'
import StoreFeed from '@/views/StoreFeed'
import StoreFeedSelect from '@/views/StoreFeedSelect'
import TransactionLookup from '@/views/TransactionLookup'
import TransactionResults from '@/views/TransactionResults'
import UpdatePassword from '@/views/UpdatePassword'
import UserSearch from '@/views/UserSearch'
import UserSearchResults from '@/views/UserSearchResults'
import UserSearchView from '@/views/UserSearchView'
import UserUsageReport from '@/views/UserUsageReport'
import UserUsageSearch from '@/views/UserUsageSearch'
import Verify from '@/views/Verify'
import Vue from 'vue'
import WatchHits from '@/views/WatchHits'
import WatchList from '@/views/WatchList'
import AdminEditProfile from '@/views/AdminEditProfile'
import AdminEditAgency from '@/views/AdminEditAgency'
import AdminEditStatus from '@/views/AdminEditStatus'
import DashboardSettings from '@/views/DashboardSettings'
import StoreSummarySearch from '@/views/StoreSummarySearch'
import StoreSummaryResults1 from '@/views/StoreSummaryResults1'
import StoreSummaryResults2 from '@/views/StoreSummaryResults2'
import Help from '@/views/Help'

import {
  basic,
  admin,
  status,
  internalAdmin,
  sworn,
} from '@/lib/plugins/authorization'
import { globalError } from '@/lib/utils'

Vue.use(Router)

function authHook(guard, swornRequired, next) {
  if (!swornRequired && guard()) next()
  else if (swornRequired  && sworn() && guard()) next()
  else next('/dashboard')
}

// If the user is visiting the login page, let them through.
// If their account is active, let them through.
// If the user is logged out, go to login page.
// If the user is not active, redirect to login with error.
function activeHook(to, next) {
  let st = status()
  if (
    [
      'login',
      'registration',
      'complete_signup',
      'verify',
      'forgot_username',
      'forgot_password',
      'update_password',
    ].includes(to.name)
  )
    next()
  else if (st === 'active') next()
  // If the user is attempting to visit a non-login page and they are logged out, schedule it for a redirect
  else if (st === 'logged_out')
    next({ path: 'login', query: { redirect: to.path } })
  else if (st === 'unapproved' || st === 'revoked') {
    globalError(
      null,
      'Your account is currently inactive. Please speak to your administrator to activate the account.',
    )
    next('/login')
  } else next()
}

let router = new Router({
  routes: [
    {
      path: '/login',
      name: 'login',
      component: Login,
    },
    {
      path: '/registration',
      name: 'registration',
      component: Registration,
    },
    {
      path: '/verify',
      name: 'verify',
      component: Verify,
    },
    {
      path: '/complete_signup',
      name: 'complete_signup',
      component: CompleteSignup,
    },
    {
      path: '/forgot_username',
      name: 'forgot_username',
      component: ForgotUsername,
    },
    {
      path: '/forgot_password',
      name: 'forgot_password',
      component: ForgotPassword,
    },
    {
      path: '/update_password',
      name: 'update_password',
      component: UpdatePassword,
    },
    {
      path: '/dashboard_settings',
      name: 'dashboard_settings',
      component: DashboardSettings,
      beforeEnter: (_to, _from, next) => authHook(internalAdmin, false, next),
    },
    {
      path: '/person_search',
      name: 'person_search',
      component: PersonSearch,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/search_results',
      name: 'search_results',
      component: SearchResults,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/my_searches',
      name: 'my_searches',
      component: MySearches,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/person_summary/:id',
      name: 'person_summary',
      component: PersonSummary,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/person_summary/:id/split_by_id',
      name: 'split_by_id',
      component: SplitById,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/split_by_id/:id',
      name: 'split_by_id_updates',
      component: SplitByIdResults,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/person_summary/:id/split_by_bio',
      name: 'split_by_bio',
      component: SplitByBio,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/split_by_bio/:id',
      name: 'split_by_bio_updates',
      component: SplitByBioResults,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/person_summary/:id/split_twins',
      name: 'split_twins',
      component: SplitTwins,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/my_reports',
      name: 'my_reports',
      component: MyReports,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/proximity_results/:id',
      name: 'proximity_results',
      component: ProximityResults,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/add_watch',
      name: 'add_watch',
      component: AddWatch,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/edit_watch/:watchSid',
      name: 'edit_watch',
      component: AddWatch,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/watch_hits/:id',
      name: 'watch_hits',
      component: WatchHits,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/store_activity_report/:id',
      name: 'store_activity_report',
      component: StoreActivityReport,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/watch_list',
      name: 'watch_list',
      component: WatchList,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/my_account',
      name: 'my_account',
      component: MyAccount,
      beforeEnter: (_to, _from, next) => authHook(basic, false, next),
    },
    {
      path: '/edit_profile',
      name: 'edit_profile',
      component: EditProfile,
      beforeEnter: (_to, _from, next) => authHook(basic, false, next),
    },
    {
      path: '/change_password',
      name: 'change_password',
      component: ChangePassword,
      beforeEnter: (_to, _from, next) => authHook(basic, false, next),
    },
    {
      path: '/store_feed/select',
      name: 'store_feed_select',
      component: StoreFeedSelect,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/store_feed',
      name: 'store_feed',
      component: StoreFeed,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/transaction_lookup',
      name: 'transaction_lookup',
      component: TransactionLookup,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/transaction_results/:id',
      name: 'transaction_results',
      component: TransactionResults,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/multiple_id',
      name: 'multiple_id_search',
      component: MultipleIdSearch,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/multiple_id/:id',
      name: 'multiple_id_results',
      component: MultipleIdResults,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/store_activity_search',
      name: 'store_activity_search',
      component: StoreActivitySearch,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/geo_activity_search',
      name: 'geo_activity_search',
      component: GeoActivitySearch,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/geoactivity/:id',
      name: 'geo_activity_results',
      component: GeoActivityResults,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/transaction_summary',
      name: 'transaction_summary',
      component: StoreSummarySearch,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/transaction_summary/1/:id',
      name: 'transaction_summary_results1',
      component: StoreSummaryResults1,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/transaction_summary/2',
      name: 'transaction_summary_results2',
      component: StoreSummaryResults2,
      beforeEnter: (_to, _from, next) => authHook(basic, true, next),
    },
    {
      path: '/dashboard',
      alias: '/',
      name: 'dashboard',
      component: Dashboard,
      beforeEnter: (_to, _from, next) => authHook(basic, false, next),
    },
    {
      path: '/admin/add_new_user',
      name: 'add_new_user',
      component: AddNewUser,
      beforeEnter: (_to, _from, next) => authHook(admin, false, next),
    },
    {
      path: '/admin/user_search',
      name: 'user_search',
      component: UserSearch,
      beforeEnter: (_to, _from, next) => authHook(admin, false, next),
    },
    {
      path: '/admin/user_search_results',
      name: 'user_search_results',
      component: UserSearchResults,
      beforeEnter: (_to, _from, next) => authHook(admin, false, next),
    },
    {
      path: '/admin/user_search_view',
      name: 'user_search_view',
      component: UserSearchView,
      beforeEnter: (_to, _from, next) => authHook(admin, false, next),
    },
    {
      path: '/admin/user_usage_search',
      name: 'user_usage_search',
      component: UserUsageSearch,
      beforeEnter: (_to, _from, next) => authHook(admin, false, next),
    },
    {
      path: '/admin/user_usage_report',
      name: 'user_usage_report',
      component: UserUsageReport,
      beforeEnter: (_to, _from, next) => authHook(admin, false, next),
    },
    {
      path: '/admin/agency_usage_search',
      name: 'agency_usage_search',
      component: AgencyUsageSearch,
      beforeEnter: (_to, _from, next) => authHook(admin, false, next),
    },
    {
      path: '/admin/agency_usage_report',
      name: 'agency_usage_report',
      component: AgencyUsageReport,
      beforeEnter: (_to, _from, next) => authHook(admin, false, next),
    },
    {
      path: '/admin/edit_profile',
      name: 'admin_edit_profile',
      component: AdminEditProfile,
      beforeEnter: (_to, _from, next) => authHook(admin, false, next),
    },
    {
      path: '/admin/edit_agency',
      name: 'admin_edit_agency',
      component: AdminEditAgency,
      beforeEnter: (_to, _from, next) => authHook(admin, false, next),
    },
    {
      path: '/admin/edit_status',
      name: 'admin_edit_status',
      component: AdminEditStatus,
      beforeEnter: (_to, _from, next) => authHook(admin, false, next),
    },
    {
      path: '/help',
      name: 'help',
      component: Help,
    },
  ],
})

// This hook must be first so that it intercepts the path properly to store
// it for redirects.
router.beforeEach((to, _from, next) => {
  if (to.name !== 'login') {
    window.localStorage.setItem('redirect-url', to.path)
  }
  next()
})

router.beforeEach((to, _from, next) => activeHook(to, next))

export default router
